import {useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {getCategoryIcon} from '@/components/CategoryIcon';
import {Typography} from '@/components/Typography';
import {KodataStoreData} from '@/map/@types';
import {MAX_VISIBLE_ZOOM_METER} from '@/map/constants';
import {useVisibleZoomLevel} from '@/map/hooks';
import {
  BackgroundImage,
  Flexbox,
  FlexFullGrow,
  interceptProps,
} from '@/styled-components';
import {palette} from '@/styles';
import {getCategoryId, withComma} from '@/utils';
import {StoreSpotMarker} from '../Markers/StoreSpotMarker';
import {OutLink, OutLinkIcon} from './StyledComponents';

interface IProps {
  item: KodataStoreData;
}

export const TopSalesStoreListItem = ({item}: IProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hover, setHover] = useState<boolean>();
  const isVisibleLevel = useVisibleZoomLevel(MAX_VISIBLE_ZOOM_METER.find_store);

  if (!item) {
    return null;
  }

  const onLinkClick = (e: MouseEvent | any) => {
    e.stopPropagation();
    window.open(`https://map.naver.com/p/search/${item.brandName}`, '_blank');
  };

  const handleMouseEnter = () => setHover(true);

  const handleMouseLeave = () => setHover(false);

  const onItemClick = () => {
    navigate({
      pathname: location.pathname + '/' + item.id,
      search: location.search,
    });
  };

  let category = item.businessItemName;

  if (category === '카페') category = '커피';

  const categoryId = getCategoryId(category);
  const icon = getCategoryIcon(categoryId, 22);

  return (
    <>
      <Root
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={onItemClick}
      >
        <Flexbox>
          <Icon src={icon} />
          <Typography
            color={palette.textPrimary}
            variant="medium14"
            fontWeight="700"
            ellipsis
          >
            {item.brandName}
          </Typography>
          <FlexFullGrow />
          <Text color={palette.textSecondary} variant="medium14">
            {item.est3AverageSales > 0
              ? `${withComma(Math.round(item.est3AverageSales))}원`
              : '-'}
          </Text>
        </Flexbox>
        <SubRow>
          <FlexFullGrow />
          <OutLink onClick={onLinkClick}>
            네이버 지도
            <OutLinkIcon />
          </OutLink>
        </SubRow>
      </Root>
      {isVisibleLevel && hover && (
        <StoreSpotMarker item={item} preventPanToAction />
      )}
    </>
  );
};

const Root = styled.div`
  cursor: pointer;
  padding: 12px 16px;
  font-weight: 500;
  &:hover {
    background-color: ${palette.gray100};
    ${OutLink} {
      display: flex;
    }
  }
  ${OutLink} {
    display: none;
  }
`;

const SubRow = styled(Flexbox)`
  height: 16px;
  margin-top: 2px;
  padding-left: 26px;
`;

const Icon = styled(BackgroundImage).withConfig({
  shouldForwardProp: interceptProps(['src']),
})<{
  src: string;
}>`
  width: 22px;
  height: 22px;
  margin-right: 4px;
  border-radius: 100%;
  ${({src}) => src && `background-image: url(${src});`}
  background-size: cover;
  flex-shrink: 0;
`;

const Text = styled(Typography)`
  white-space: nowrap;
`;
